/**
 * Default office for one-office pages (MODULE-AUDIT X7). Schedule, Lab,
 * Referrals, Documents, Timesheets and the other `officeScope: 'location'`
 * routes used to stop at a "Pick an office" wall whenever the selector was
 * "All offices", including a dead end inside a patient (Patient >
 * Documents). Now the shell opens them at the best office it knows:
 *
 *   1. the selected patient's home office (the page is about them),
 *   2. the office this person last used for this module on this device,
 *   3. their home office (the bootstrap's `defaultLocationId`),
 *   4. their only office.
 *
 * Each candidate must be one of the person's offices and, when the route
 * has a permission, an office where they hold it. Nothing matches: the
 * picker stays (it still lists every office).
 */
import { readString, writeString } from '../lib/storage.js';

export type DefaultOfficeReason = 'patient' | 'last' | 'home' | 'only';

export interface DefaultOfficeInput {
  offices: readonly { id: string }[];
  patientLocationId?: string | null | undefined;
  lastLocationId?: string | null | undefined;
  homeLocationId?: string | null | undefined;
  /** The route's permission holds at this office (default: every office). */
  allowed?: (locationId: string) => boolean;
}

export function defaultOfficeFor(
  input: DefaultOfficeInput,
): { locationId: string; reason: DefaultOfficeReason } | null {
  const ok = (id: string | null | undefined): id is string =>
    typeof id === 'string' &&
    input.offices.some((o) => o.id === id) &&
    (input.allowed ? input.allowed(id) : true);
  if (ok(input.patientLocationId))
    return { locationId: input.patientLocationId, reason: 'patient' };
  if (ok(input.lastLocationId)) return { locationId: input.lastLocationId, reason: 'last' };
  if (ok(input.homeLocationId)) return { locationId: input.homeLocationId, reason: 'home' };
  const usable = input.offices.filter((o) => ok(o.id));
  if (usable.length === 1 && usable[0]) return { locationId: usable[0].id, reason: 'only' };
  return null;
}

/** Why the office was chosen, for the toast ("your home office"). */
export const DEFAULT_OFFICE_WHY: Record<DefaultOfficeReason, string> = {
  patient: "the patient's office",
  last: 'the office you used last here',
  home: 'your home office',
  only: 'your office',
};

/** Office ids are not PHI; kept per practice, user and module on this device. */
export function lastOfficeKey(practiceId: string, userId: string, moduleId: string): string {
  return `velora.lastOffice.${practiceId}.${userId}.${moduleId}`;
}

export function readLastOffice(practiceId: string, userId: string, moduleId: string) {
  return readString(lastOfficeKey(practiceId, userId, moduleId));
}

export function rememberLastOffice(
  practiceId: string,
  userId: string,
  moduleId: string,
  locationId: string,
): void {
  writeString(lastOfficeKey(practiceId, userId, moduleId), locationId);
}
